import { useQuery } from 'react-query'
import { fetchMyApplication } from 'services/affiliates'
import * as cookiesService from 'services/cookies'
const APPLICATION_URL = import.meta.env.VITE_AFFILIATE_APPLICATION_URL

export enum ApplicationItemStatus {
  DEFAULT,
  SUBMITTED,
  WAITING,
  DONE,
}

function application(rawStatus: string): {
  status: ApplicationItemStatus
  rawStatus: string
  description: string
  actionText?: string
  actionUrl?: string
} {
  switch (rawStatus) {
    case 'Started':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus: rawStatus,
        description: rawStatus,
      }
    case 'Submitted':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus: rawStatus,
        description: rawStatus,
      }
    case 'Submit Name Request':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus: rawStatus,
        description: rawStatus,
      }
    case 'Name Pending Approval':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus: rawStatus,
        description: rawStatus,
      }
    case 'Done':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus: rawStatus,
        description: rawStatus,
      }
    case 'Incorrect or Missing Information':
      return {
        status: ApplicationItemStatus.WAITING,
        rawStatus,
        description: rawStatus,
        actionText: 'Update Application',
        actionUrl: APPLICATION_URL,
      }
    default:
      return {
        status: ApplicationItemStatus.DEFAULT,
        rawStatus: rawStatus,
        description: 'Submit Name Request(s)',
      }
  }
}

function location(rawStatus: string): {
  status: ApplicationItemStatus
  rawStatus: string
  description: string
  actionText?: string
  actionUrl?: string
} {
  switch (rawStatus) {
    case 'Incorrect or Missing Information':
      return {
        status: ApplicationItemStatus.WAITING,
        rawStatus,
        description: rawStatus,
        actionText: 'Update Application',
        actionUrl: APPLICATION_URL,
      }
    case 'Submitted':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus,
        description: rawStatus,
      }
    case 'Pending Approval':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus,
        description: rawStatus,
      }
    case 'Done':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    case 'Not Required':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    case 'Covered by HQ':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    default:
      return {
        status: ApplicationItemStatus.DEFAULT,
        rawStatus,
        description: rawStatus,
      }
  }
}

function insurance(rawStatus: string): {
  status: ApplicationItemStatus
  rawStatus: string
  description: string
  actionText?: string
  actionUrl?: string
} {
  switch (rawStatus) {
    case 'Incorrect or Missing Information':
      return {
        status: ApplicationItemStatus.WAITING,
        rawStatus,
        description: rawStatus,
        actionText: 'Update Application',
        actionUrl: APPLICATION_URL,
      }
    case 'Pending Approval':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus,
        description: rawStatus,
      }
    case 'Done':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    case 'Not Required':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    case 'Covered by HQ':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    default:
      return {
        status: ApplicationItemStatus.DEFAULT,
        rawStatus,
        description: 'Submit Insurance',
      }
  }
}

function payment(rawStatus: string): {
  status: ApplicationItemStatus
  rawStatus: string
  description: string
  actionText?: string
  actionUrl?: string
} {
  switch (rawStatus) {
    case 'Submit Payment':
      return {
        status: ApplicationItemStatus.WAITING,
        rawStatus,
        description: rawStatus,
      }
    case 'Pending Approval':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus,
        description: rawStatus,
      }
    case 'Processing':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus,
        description: rawStatus,
      }
    case 'Done':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    case 'Fees Waived':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    case 'Declined':
      return {
        status: ApplicationItemStatus.WAITING,
        rawStatus,
        description: rawStatus,
        actionText: 'Update Payment',
        actionUrl: APPLICATION_URL,
      }
    default:
      return {
        status: ApplicationItemStatus.DEFAULT,
        rawStatus,
        description: 'Submit Payment',
      }
  }
}

function license(rawStatus: string): {
  status: ApplicationItemStatus
  rawStatus: string
  description: string
  actionText?: string
  actionUrl?: string
} {
  switch (rawStatus) {
    case 'Processing':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus,
        description: rawStatus,
      }
    case 'Generating Agreement':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus,
        description: rawStatus,
      }
    case 'Signature Required':
      return {
        status: ApplicationItemStatus.WAITING,
        rawStatus,
        description: rawStatus,
        actionText: 'Signature Required',
        actionUrl: APPLICATION_URL,
      }
    case 'Pending Approval':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus,
        description: rawStatus,
      }
    case 'Done':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    default:
      return {
        status: ApplicationItemStatus.DEFAULT,
        rawStatus,
        description: 'E-sign annual license agreement.',
      }
  }
}

function activation(rawStatus: string): {
  status: ApplicationItemStatus
  rawStatus: string
  description: string
} {
  switch (rawStatus) {
    case 'Processing':
      return {
        status: ApplicationItemStatus.SUBMITTED,
        rawStatus,
        description: rawStatus,
      }
    case 'Done':
      return {
        status: ApplicationItemStatus.DONE,
        rawStatus,
        description: rawStatus,
      }
    default:
      return {
        status: ApplicationItemStatus.DEFAULT,
        rawStatus,
        description: '-',
      }
  }
}

export function useAffiliateApplication() {
  const accessToken = cookiesService.getAccessToken()
  return useQuery(
    'affiliateApplication',
    () =>
      fetchMyApplication().then((r) => ({
        application: application(r.application),
        location: location(r.location),
        insurance: insurance(r.insurance),
        payment: payment(r.payment),
        license: license(r.license),
        activation: activation(r.activation),
      })),
    {
      enabled: !!accessToken,
    },
  )
}
